if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');
      this.cartDrawer = document.querySelector('cart-drawer');

      this.cartUrl = this.dataset.cartUrl || '/cart';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      const submitButton = this.querySelector('[type="submit"]');
      submitButton.setAttribute('disabled', true);
      submitButton.classList.add('button--loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      // Build sections to fetch - check if components exist and have the method
      const sectionsToFetch = [];
      
      if (this.cartDrawer && typeof this.cartDrawer.getSectionsToRender === 'function') {
        sectionsToFetch.push(...this.cartDrawer.getSectionsToRender().map((section) => section.id));
      }
      
      if (this.cartNotification && typeof this.cartNotification.getSectionsToRender === 'function') {
        sectionsToFetch.push(...this.cartNotification.getSectionsToRender().map((section) => section.id));
      }

      const formData = new FormData(this.form);
      if (sectionsToFetch.length > 0) {
        formData.append('sections', sectionsToFetch);
      }
      formData.append('sections_url', window.location.pathname);

      // If this product supports upsell bundles, add a unique property to force a separate line item
      // and add attribute for cart transform to recognize it
      const bundleData = this.dataset.bundle?.trim();
      if (bundleData) {
        const uniqueId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        formData.append('properties[_bundle_request_id]', uniqueId);
        formData.append('attributes[_upsell_source]', 'modal');
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status == 422) {
            // When quantity error
            if (this.cartNotification) {
              if (typeof this.cartNotification.setActiveElement === 'function') {
                this.cartNotification.setActiveElement(document.activeElement);
              }
              if (typeof this.cartNotification.renderQuantityError === 'function') {
                this.cartNotification.renderQuantityError(formData.get('inventory_quantity'));
              }
            }
            return;
          }

          if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
            this.cartDrawer.renderContents(parsedState);
          }

          switch (submitButton.dataset.addToCartBehavior) {
            case 'open_cart_drawer':
              // Set timeout to force animation. Because content is just updated with renderContents
              if (this.cartDrawer && typeof this.cartDrawer.open === 'function') {
                setTimeout(() => {
                  this.cartDrawer.open();
                });
              }
              break;
            case 'show_cart_notification':
              if (this.cartNotification) {
                if (typeof this.cartNotification.setActiveElement === 'function') {
                  this.cartNotification.setActiveElement(document.activeElement);
                }
                if (typeof this.cartNotification.renderContents === 'function') {
                  this.cartNotification.renderContents(parsedState);
                }
              }
              break;
            case 'go_to_cart_page': {
              this.cartUrl = this.getAttribute('this-cart-url') || '/cart';

              const handledByUpsell = this.tryShowUpsellModal(formData);
              if (!handledByUpsell) {
                window.location.href = this.cartUrl;
              }
              break;
            }
            default:
              if (this.cartNotification) {
                if (typeof this.cartNotification.setActiveElement === 'function') {
                  this.cartNotification.setActiveElement(document.activeElement);
                }
                if (typeof this.cartNotification.renderContents === 'function') {
                  this.cartNotification.renderContents(parsedState);
                }
              }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButton.classList.remove('button--loading');
          submitButton.removeAttribute('disabled');
        });
    }
    
    tryShowUpsellModal(formData) {
      const bundleData = this.dataset.bundle?.trim();

      if (!bundleData) {
        return false;
      }

      const upsellModal = this.getUpsellModalElement();
      if (!upsellModal) {
        return false;
      }

      const upsellData = this.buildUpsellData(formData, bundleData);

      if (this.isUpsellModalReady(upsellModal)) {
        this.showUpsellModal(upsellModal, upsellData);
      } else {
        this.deferUpsellModal(upsellData);
      }

      return true;
    }

    getUpsellModalElement() {
      return document.querySelector('upsell-modal');
    }

    buildUpsellData(formData, bundleData) {
      const quantity = parseInt(formData.get('quantity') || '1', 10);
      const variantId = formData.get('id');
      const addedProduct = this.collectAddedProductDetails(quantity);
      const parentHandle = this.dataset.productHandle || null;
      const bundleRequestId = formData.get('properties[_bundle_request_id]') || null;

      return {
        handles: bundleData,
        addedProduct,
        variantId,
        quantity,
        parentHandle,
        bundleRequestId,
        redirectUrl: this.cartUrl,
        redirectOnClose: true
      };
    }

    isUpsellModalReady(modal) {
      return typeof modal.showWithHandles === 'function';
    }

    showUpsellModal(modal, data) {
      modal.showWithHandles(data);
    }

    deferUpsellModal(data) {
      if (!window.customElements?.whenDefined) {
        return false;
      }

      sessionStorage.setItem('deferredUpsell', JSON.stringify(data));

      const timeoutId = this.setUpsellTimeout();

      window.customElements.whenDefined('upsell-modal')
        .then(() => this.handleDeferredUpsell(timeoutId))
        .catch(() => this.handleUpsellError(timeoutId));
    }

    setUpsellTimeout() {
      return setTimeout(() => {
        sessionStorage.removeItem('deferredUpsell');
        window.location.href = this.cartUrl;
      }, 2000);
    }

    handleDeferredUpsell(timeoutId) {
      const modal = this.getUpsellModalElement();

      if (!modal || !this.isUpsellModalReady(modal)) {
        return this.handleUpsellError(timeoutId);
      }

      const storedData = this.retrieveDeferredUpsellData();
      if (!storedData) {
        return this.handleUpsellError(timeoutId);
      }

      clearTimeout(timeoutId);
      this.showUpsellModal(modal, storedData);
      sessionStorage.removeItem('deferredUpsell');
    }

    retrieveDeferredUpsellData() {
      try {
        const storedDetail = sessionStorage.getItem('deferredUpsell');
        return storedDetail ? JSON.parse(storedDetail) : null;
      } catch {
        return null;
      }
    }

    handleUpsellError(timeoutId) {
      clearTimeout(timeoutId);
      window.location.href = this.cartUrl;
    }

    collectAddedProductDetails(quantity = 1) {
      const formRoot = this.closest('[data-product-card]') || this;

      return {
        title: this.extractProductTitle(formRoot) || 'Product successfully added',
        image: this.extractProductImage(formRoot),
        price: this.extractProductPrice(formRoot),
        quantity,
        handle: this.dataset.productHandle || null
      };
    }

    extractProductTitle(root) {
      const selectors = '[data-product-title], .product__title, h1.product__title';
      const titleElement = root.querySelector(selectors) || document.querySelector(selectors);
      return titleElement?.textContent?.trim() || null;
    }

    extractProductImage(root) {
      const selectors = 'img[data-product-featured-image], .product-media img, .swiper-slide-active img, .product-image img';
      const imageElement = root.querySelector(selectors) || document.querySelector(selectors);
      return imageElement?.currentSrc || imageElement?.src || imageElement?.dataset?.src || null;
    }

    extractProductPrice(root) {
      const selectors = '[data-price-final], .product__price .price__item--sale, .product__price .price__item:last-child';
      const priceElement = root.querySelector(selectors) || document.querySelector(selectors);
      return priceElement?.textContent?.trim() || null;
    }

    formatMoney(cents) {
      // Simple money formatting helper
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format(cents / 100);
    }
  }

  window.ProductForm = ProductForm;
  customElements.define('product-form', ProductForm);
}

if (!customElements.get('sticky-product-form')) {
  /**
   * Defines a custom element 'sticky-product-form' if not already defined.
   */
  customElements.define(
    'sticky-product-form',
    class StickyProductForm extends HTMLElement {
      /**
       * Constructor for StickyProductForm class.
       * Initializes references to several elements.
       */
      constructor() {
        super();
        this.productInfo = document.querySelector('[data-info-form]');
        this.productSelector = this.querySelector('[data-product-selector]');
        this.expandButton = this.querySelector('[data-variant-expand]');
        this.closeButton = this.querySelector('[data-variant-close]');
      }

      /**
       * Called when the element is added to the DOM.
       * Sets up IntersectionObserver and event listeners for expand and close buttons.
       */
      connectedCallback() {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.isIntersecting ? this.toggleStickyForm(true) : this.toggleStickyForm(false);
          });
        });

        this.productInfo && this.observer.observe(this.productInfo);

        this.expandButton?.addEventListener('click', this.toggleProductSelector.bind(this));
        this.closeButton?.addEventListener('click', this.toggleProductSelector.bind(this));
      }

      /**
       * Toggles the sticky form visibility state
       * @param {boolean} hide - Whether to hide the form
       */
      toggleStickyForm(hide) {
        this.classList.toggle('translate-y-full', hide);
        document.body.classList.toggle('sticky-product-form', !hide);
      }

      /**
       * Called when the element is removed from the DOM.
       * Cleans up IntersectionObserver and event listeners.
       */
      disconnectedCallback() {
        this.productInfo && this.observer.disconnect();

        this.expandButton?.removeEventListener('click', this.toggleProductSelector.bind(this));
        this.closeButton?.removeEventListener('click', this.toggleProductSelector.bind(this));
        document.removeEventListener('click', this.closeDrawer.bind(this));
      }

      /**
       * Toggles the visibility of the product selector.
       * Adds or removes the 'expanded' class and sets up or removes a document click listener.
       */
      toggleProductSelector() {
        this.classList.toggle('expanded');

        this.classList.contains('expanded')
          ? document.addEventListener('click', this.closeDrawer.bind(this))
          : document.removeEventListener('click', this.closeDrawer.bind(this));
      }

      /**
       * Closes the product selector if a click event occurs outside the element.
       * @param {Event} event - The click event.
       */
      closeDrawer(event) {
        if (!this.contains(event.target)) {
          this.classList.remove('expanded');
          document.removeEventListener('click', this.closeDrawer.bind(this));
        }
      }
    }
  );
}
